import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface SavedAnnotationsOverlayProps {
  viewer: any
  annotations: any[]
  hiddenAnnotations?: Set<string>
  onAnnotationClick?: (annotation: any) => void
}

interface TooltipState {
  visible: boolean
  x: number
  y: number
  annotation: any
}

const SavedAnnotationsOverlay: React.FC<SavedAnnotationsOverlayProps> = ({
  viewer,
  annotations,
  hiddenAnnotations,
  onAnnotationClick
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, annotation: null })
  const animationFrameRef = useRef<number | null>(null)
  const isDrawingRef = useRef(false)

  useEffect(() => {
    // Reduced logging for performance
    if (annotations.length > 0) {
      logger.debug('🔵 SavedAnnotationsOverlay mounted, annotations count:', annotations.length)
    }

    if (!viewer || !window.OpenSeadragon) {
      return
    }

    // Create or get canvas overlay
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas')
      canvasRef.current.style.position = 'absolute'
      canvasRef.current.style.top = '0'
      canvasRef.current.style.left = '0'
      canvasRef.current.style.pointerEvents = 'none' // Disable pointer events - let OpenSeadragon handle it
      canvasRef.current.style.cursor = 'default'
    }

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Helper function to convert image pixel coordinates to canvas pixel coordinates
    const imagePixelToCanvasPixel = (imageX: number, imageY: number) => {
      // Use viewport.imageToViewportCoordinates (not tiledImage)
      const imagePoint = new window.OpenSeadragon.Point(imageX, imageY)
      const viewportPoint = viewer.viewport.imageToViewportCoordinates(imagePoint)

      // Convert viewport to canvas pixel coordinates
      const canvasPixel = viewer.viewport.pixelFromPoint(viewportPoint)

      // Removed excessive logging
      return canvasPixel
    }

    // Function to draw annotations on canvas
    const drawAnnotations = () => {
      // Prevent concurrent draws
      if (isDrawingRef.current) return
      isDrawingRef.current = true

      // Get container size
      const container = viewer.canvas
      canvas.width = container.clientWidth
      canvas.height = container.clientHeight

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Skip drawing if no annotations
      if (annotations.length === 0) {
        isDrawingRef.current = false
        return
      }

      // Draw each annotation (skip hidden ones)
      annotations.forEach((ann) => {
        if (!ann.coordinates) return
        if (hiddenAnnotations?.has(ann.id)) return

        try {
          const shapeData = ann.coordinates
          const color = ann.color || '#00ff00'
          const strokeWidth = ann.stroke_width || 3

          ctx.strokeStyle = color
          ctx.fillStyle = color + '33'
          ctx.lineWidth = strokeWidth
          ctx.shadowBlur = 0

          if (shapeData.type === 'arrow' || shapeData.type === 'line') {
            // Convert image pixel coordinates to canvas pixel coordinates
            const p1 = imagePixelToCanvasPixel(shapeData.x1, shapeData.y1)
            const p2 = imagePixelToCanvasPixel(shapeData.x2, shapeData.y2)

            // Draw line
            ctx.beginPath()
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()

            // Draw arrowhead
            const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x)
            const arrowLength = 15
            const arrowWidth = 10

            ctx.beginPath()
            ctx.moveTo(p2.x, p2.y)
            ctx.lineTo(
              p2.x - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle),
              p2.y - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle)
            )
            ctx.lineTo(
              p2.x - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle),
              p2.y - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle)
            )
            ctx.closePath()
            ctx.fillStyle = color
            ctx.fill()

          } else if (shapeData.type === 'rectangle') {
            const topLeft = imagePixelToCanvasPixel(shapeData.x, shapeData.y)
            const bottomRight = imagePixelToCanvasPixel(
              shapeData.x + shapeData.width,
              shapeData.y + shapeData.height
            )

            const width = bottomRight.x - topLeft.x
            const height = bottomRight.y - topLeft.y

            ctx.strokeRect(topLeft.x, topLeft.y, width, height)
            ctx.fillRect(topLeft.x, topLeft.y, width, height)

          } else if (shapeData.type === 'circle') {
            const center = imagePixelToCanvasPixel(shapeData.cx, shapeData.cy)
            const edge = imagePixelToCanvasPixel(shapeData.cx + shapeData.r, shapeData.cy)
            const radius = Math.abs(edge.x - center.x)

            ctx.beginPath()
            ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI)
            ctx.fill()
            ctx.stroke()

          } else if (shapeData.type === 'ellipse') {
            const center = imagePixelToCanvasPixel(shapeData.cx, shapeData.cy)
            const rxPoint = imagePixelToCanvasPixel(shapeData.cx + shapeData.rx, shapeData.cy)
            const ryPoint = imagePixelToCanvasPixel(shapeData.cx, shapeData.cy + shapeData.ry)

            const rx = Math.abs(rxPoint.x - center.x)
            const ry = Math.abs(ryPoint.y - center.y)

            ctx.beginPath()
            ctx.ellipse(center.x, center.y, rx, ry, 0, 0, 2 * Math.PI)
            ctx.fill()
            ctx.stroke()
          }
        } catch (error) {
          console.error('Error drawing annotation:', error, ann)
        }
      })

      isDrawingRef.current = false
    }

    // Add canvas to viewer - make sure it's positioned correctly
    const viewerElement = viewer.element
    if (!viewerElement) {
      return
    }
    if (!viewerElement.contains(canvas)) {
      canvas.style.zIndex = '10'
      viewerElement.appendChild(canvas)
    }

    // Initial draw
    drawAnnotations()

    // No mouse event listeners on canvas - it's pure visual overlay with pointerEvents: none

    // Throttled redraw - only on animation-finish to avoid blocking during pan/zoom
    const handleViewportChange = () => {
      // Cancel previous animation frame if exists
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      // Schedule new draw on next frame
      animationFrameRef.current = requestAnimationFrame(() => {
        drawAnnotations()
        // Removed setViewportVersion to avoid triggering React re-renders
        animationFrameRef.current = null
      })
    }

    // IMPORTANT: Only listen to animation-finish, NOT animation
    // 'animation' fires continuously during pan/zoom and blocks interaction
    // 'animation-finish' only fires when pan/zoom stops
    viewer.addHandler('animation-finish', handleViewportChange)
    viewer.addHandler('resize', handleViewportChange)

    return () => {
      // Cancel any pending animation frame
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      if (!viewer) return
      viewer.removeHandler('animation-finish', handleViewportChange)
      viewer.removeHandler('resize', handleViewportChange)

      if (canvas && viewerElement.contains(canvas)) {
        viewerElement.removeChild(canvas)
      }
    }
  }, [viewer, annotations, hiddenAnnotations])

  // Handle annotation click with delete
  const handleAnnotationClick = async (annotation: any, e: React.MouseEvent) => {
    e.stopPropagation()

    if (onAnnotationClick) {
      onAnnotationClick(annotation)
    }

    const shouldDelete = confirm(`Delete annotation "${annotation.text_content || 'Unnamed'}"?`)
    if (shouldDelete) {
      try {
        const response = await fetch(`/api/v1/pathology/annotations/${annotation.id}/`, {
          method: 'DELETE',
        })

        if (response.ok) {
          toast.success('Annotation deleted')
          window.location.reload()
        } else {
          toast.error('Failed to delete annotation')
        }
      } catch (error) {
        console.error('Error deleting annotation:', error)
        toast.error('Error deleting annotation')
      }
    }
  }

  // Render interactive SVG overlay on top of canvas
  const renderInteractiveSVG = () => {
    if (!viewer || !annotations || annotations.length === 0) return null

    return (
      <svg
        className="absolute top-0 left-0 w-full h-full"
        style={{ zIndex: 11, pointerEvents: 'none' }}
      >
        {annotations.map((ann) => {
          if (!ann.coordinates || hiddenAnnotations?.has(ann.id)) return null

          const shapeData = ann.coordinates

          try {
            if (shapeData.type === 'rectangle') {
              const imagePoint1 = new (window as any).OpenSeadragon.Point(shapeData.x, shapeData.y)
              const imagePoint2 = new (window as any).OpenSeadragon.Point(
                shapeData.x + shapeData.width,
                shapeData.y + shapeData.height
              )

              const vp1 = viewer.viewport.imageToViewportCoordinates(imagePoint1)
              const vp2 = viewer.viewport.imageToViewportCoordinates(imagePoint2)

              const pixel1 = viewer.viewport.pixelFromPoint(vp1)
              const pixel2 = viewer.viewport.pixelFromPoint(vp2)

              return (
                <rect
                  key={ann.id}
                  x={pixel1.x}
                  y={pixel1.y}
                  width={pixel2.x - pixel1.x}
                  height={pixel2.y - pixel1.y}
                  fill="transparent"
                  stroke="transparent"
                  strokeWidth={10}
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  onClick={(e) => handleAnnotationClick(ann, e)}
                  onMouseEnter={(e) => {
                    setTooltip({
                      visible: true,
                      x: e.clientX,
                      y: e.clientY,
                      annotation: ann
                    })
                  }}
                  onMouseMove={(e) => {
                    setTooltip(prev => ({
                      ...prev,
                      x: e.clientX,
                      y: e.clientY
                    }))
                  }}
                  onMouseLeave={() => {
                    setTooltip({ visible: false, x: 0, y: 0, annotation: null })
                  }}
                />
              )
            }
            // Add other shape types as needed
          } catch (error) {
            console.error('Error rendering interactive annotation:', error)
          }

          return null
        })}
      </svg>
    )
  }

  return (
    <>
      {/* Interactive SVG overlay for click detection */}
      {renderInteractiveSVG()}

      {/* Tooltip */}
      {tooltip.visible && tooltip.annotation && (
        <div
          className="fixed z-50 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-2xl max-w-xs pointer-events-none"
          style={{
            left: `${tooltip.x + 15}px`,
            top: `${tooltip.y + 15}px`,
            transform: 'translate(0, 0)'
          }}
        >
          <div className="font-bold text-sm mb-1 text-yellow-400">
            {tooltip.annotation.text_content || 'Annotation'}
          </div>
          {tooltip.annotation.description && (
            <div className="text-xs text-gray-300 mb-2">
              {tooltip.annotation.description}
            </div>
          )}
          <div className="text-xs text-gray-400 border-t border-gray-700 pt-1">
            Type: {tooltip.annotation.coordinates?.type || 'unknown'}
          </div>
          <div className="text-xs text-yellow-300 mt-1 font-semibold">
            Click to delete
          </div>
        </div>
      )}
    </>
  )
}

export default SavedAnnotationsOverlay
